import React, { useState, useCallback } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { FeatherEdit2, FeatherX } from '@subframe/core';

const XRPL_ADDRESS_REGEX = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartConversation: (address: string) => void;
  currentUserAddress: string | undefined;
}

export const NewConversationModal: React.FC<NewConversationModalProps> = ({
  isOpen,
  onClose,
  onStartConversation,
  currentUserAddress,
}) => {
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const reset = useCallback(() => {
    setAddress('');
    setError('');
    setTouched(false);
  }, []);

  const validate = useCallback(
    (value: string): string => {
      const trimmed = value.trim();
      if (!trimmed) return '';
      if (!XRPL_ADDRESS_REGEX.test(trimmed)) {
        return 'Enter a valid XRPL wallet address (starts with r)';
      }
      if (currentUserAddress && trimmed === currentUserAddress) {
        return 'You cannot message yourself';
      }
      return '';
    },
    [currentUserAddress]
  );

  const isValid = address.trim().length > 0 && !validate(address);

  const handleBlur = () => {
    setTouched(true);
    if (address.trim()) {
      setError(validate(address));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setAddress(val);
    if (touched && val.trim()) {
      setError(validate(val));
    } else if (!val.trim()) {
      setError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate(address);
    if (validationError) {
      setTouched(true);
      setError(validationError);
      return;
    }
    onStartConversation(address.trim());
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <form onSubmit={handleSubmit} className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <FeatherEdit2 className="h-6 w-6 text-brand-600" />
                <span className="text-heading-2 font-heading-2 text-default-font">
                  New Message
                </span>
              </div>
              <span className="text-body font-body text-subtext-color">
                Start a conversation with any XRPL wallet
              </span>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Address Input */}
          <TextField
            className="h-auto w-full flex-none"
            label="Wallet address"
            helpText={touched && error ? error : ''}
            error={touched && !!error}
          >
            <TextField.Input
              placeholder="rXXXXX..."
              value={address}
              onChange={handleChange}
              onBlur={handleBlur}
              autoFocus
            />
          </TextField>

          {/* Action Buttons */}
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-tertiary"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              size="large"
              type="submit"
              disabled={!isValid}
            >
              Start Conversation
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};
